<template>
  <van-popup
    v-model:show="visible"
    position="center"
    round
    closeable
    class="listing-popup"
  >
    <div class="popup-content">
      <div class="popup-heading">
        <h2>Select available product</h2>
        <p>{{ productName || 'Same product from multiple stores' }}</p>
      </div>

      <div v-if="loading" class="loading-state">
        <van-loading size="28" />
        <span>Loading available products...</span>
      </div>

      <div v-else-if="errorMessage" class="empty-state">
        <van-icon name="warning-o" size="34" />
        <strong>Products could not be loaded</strong>
        <span>{{ errorMessage }}</span>
        <van-button plain type="primary" size="small" @click="$emit('retry')">Retry</van-button>
      </div>

      <div v-else-if="listings.length === 0" class="empty-state">
        <van-icon name="shop-o" size="34" />
        <strong>No products currently available</strong>
        <span>Please stay tuned.</span>
      </div>

      <div v-else class="listing-list">
        <button
          v-for="listing in listings"
          :key="listing.id"
          type="button"
          class="listing-row"
          :disabled="!isListingSelectable(listing)"
          @click="$emit('select', listing)"
        >
          <div class="listing-copy">
            <strong>{{ listing.title || `Listing ${listing.listingId}` }}</strong>
            <span v-if="listing.storeName">{{ listing.storeName }}</span>
            <em :class="stockClass(listing)">{{ stockLabel(listing) }}</em>
          </div>
          <div class="listing-action">
            <strong>{{ priceText(listing.minSalePrice) }}</strong>
            <van-icon v-if="isListingSelectable(listing)" name="arrow" size="18" />
          </div>
        </button>
      </div>

      <p v-if="listings.length > 0 && !loading" class="listing-note">
        Price and stock are confirmed again on the product page.
      </p>
    </div>
  </van-popup>
</template>

<script setup lang="ts">
import type { EventPreorderAvailableListing } from '@/api/models/event-preorder'

const props = defineProps<{
  show: boolean
  productName: string
  listings: EventPreorderAvailableListing[]
  loading: boolean
  errorMessage: string
}>()

const emit = defineEmits<{
  (event: 'update:show', value: boolean): void
  (event: 'select', listing: EventPreorderAvailableListing): void
  (event: 'retry'): void
}>()

const { $currencySymbol, $toThousandsFn } = useNuxtApp()

const visible = computed({
  get: () => props.show,
  set: (value: boolean) => emit('update:show', value)
})

const priceText = (price: number | null) => price == null
  ? '--'
  : `${$currencySymbol()} ${$toThousandsFn(price)}`

const isListingSelectable = (listing: EventPreorderAvailableListing) =>
  listing.selectable && listing.stockStatus !== 'SOLD_OUT'

const stockLabel = (listing: EventPreorderAvailableListing) => !listing.selectable
  ? 'Disabled'
  : ({
    IN_STOCK: 'In stock',
    SOLD_OUT: 'Sold out',
    UNAVAILABLE: 'Disabled'
  })[listing.stockStatus]

const stockClass = (listing: EventPreorderAvailableListing) => listing.selectable
  ? listing.stockStatus.toLowerCase().replace('_', '-')
  : 'unavailable'
</script>

<style scoped lang="scss">
.listing-popup {
  width: 1360px;
  max-width: calc(100vw - 64px);
  max-height: 82vh;
  overflow-y: auto;
  button { cursor: pointer; }
  button:disabled { cursor: not-allowed; }
  button:focus-visible { outline: 4px solid #c91d2b; outline-offset: 4px; }
}

.popup-content {
  padding: 45px 30px calc(37.5px + env(safe-area-inset-bottom));
}

.popup-heading {
  padding: 0 60px 27.5px 7.5px;

  h2 {
    margin: 0;
    color: #1e1e1e;
    font-size: 40px;
    line-height: 1.25;
  }

  p {
    margin: 7.5px 0 0;
    color: #777;
    font-size: 26.25px;
    line-height: 1.35;
    overflow-wrap: break-word;
    word-break: break-word;
  }
}

.listing-list {
  border-top: 1.25px solid #ededed;
}

.listing-row {
  width: 100%;
  min-height: 157.5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22.5px 10px;
  text-align: left;
  background: #fff;
  border: 0;
  border-bottom: 1.25px solid #ededed;

  &:disabled {
    opacity: .48;
  }
}

.listing-copy {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;

  > * + * {
    margin-top: 6.25px;
  }

  strong {
    max-width: 100%;
    color: #272727;
    font-size: 28.75px;
    line-height: 1.35;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  span {
    max-width: 100%;
    color: #777;
    font-size: 23.75px;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  em {
    padding: 2.5px 10px;
    color: #187341;
    font-size: 22.5px;
    font-style: normal;
    background: #eaf8f0;
    border-radius: 5px;

    &.sold-out,
    &.unavailable {
      color: #666;
      background: #eee;
    }
  }
}

.listing-action {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  margin-left: 25px;
  color: #c91927;

  .van-icon {
    margin-left: 10px;
  }

  strong {
    font-size: 28.75px;
    white-space: nowrap;
  }
}

.loading-state,
.empty-state {
  min-height: 350px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 43.75px;
  color: #777;
  text-align: center;

  > * + * {
    margin-top: 15px;
  }

  strong {
    color: #333;
    font-size: 31.25px;
  }

  span {
    font-size: 26.25px;
    line-height: 1.45;
  }
}

.listing-note {
  margin: 25px 0 0;
  color: #8a8a8a;
  font-size: 22.5px;
  line-height: 1.45;
  text-align: center;
}
</style>
